/**
 * Formulário curto do setor: o nome que a fábrica usa, a ETAPA da planilha que
 * alimenta a sala e se é ESTA sala que recebe os ambientes daquela etapa.
 *
 * Abre em três momentos: depois de desenhar uma sala nova, ao pedir para editar
 * uma sala existente e pelo atalho "Criar setor para <etapa>" da bandeja. São
 * só três campos de propósito — o desenho já foi feito com o dedo, e pedir
 * quinze campos depois disso é o jeito mais rápido de o gestor desistir.
 *
 * O vínculo com a etapa é o que faz o ambiente CHEGAR aqui. Por isso o campo é
 * obrigatório e, quando a etapa escolhida já tem outra sala recebendo, o aviso
 * diz o nome dela antes de o gestor marcar "recebe" — trocar a recepção sem
 * saber mudaria de lugar todos os ambientes daquela etapa.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { SecondaryButton } from "../Orcamentos/styles";
import { TIPO_ETAPA, etapasDoVinculo, gruposDeVinculo } from "./dados/etapasPlanilha";
import { rotuloDaEtapa } from "./setores";
import { AvisoForm, CampoDoSetor, FormularioDoSetor, RodapeDoSetor } from "./styles";

const LIMITE_NOME = 60;

const vazio = () => ({ nome: "", etapa: "", recebe_da_planilha: true });


/**
 * Etapa (ou grupo) que acontece AO LADO do fluxo — terceiros, acabamento,
 * insumos. Nenhum ambiente para numa sala dessas sozinho: a planilha não tira
 * o ambiente da sequência por causa delas. Dizer isso na hora de criar evita
 * a sala que nasce e nunca recebe ninguém, sem o gestor entender por quê.
 */
export function vinculoSoParalelo(codigo) {
  const etapas = etapasDoVinculo(codigo);
  return etapas.length > 0 && etapas.every((etapa) => etapa.tipo === TIPO_ETAPA.PARALELA);
}

/**
 * Aviso sobre a recepção da etapa escolhida: quem recebe hoje e o que muda.
 * `null` quando não há nada a avisar.
 */
export function avisoDaRecepcao({ etapa, recebe, catalogo, setorId }) {
  if (!etapa) return null;
  const receptor = catalogo.setorQueRecebe(etapa);
  const outras = catalogo.setoresDaEtapa(etapa).filter((setor) => setor.id !== setorId);
  if (recebe && receptor && receptor.id !== setorId) {
    return `Hoje os ambientes desta etapa chegam em "${receptor.nome}". Salvando assim, passam a chegar aqui.`;
  }
  if (!recebe && !receptor) {
    return "Nenhuma sala recebe os ambientes desta etapa: eles vão para a sala vizinha do fluxo (com o selo “aguardando”) ou, sem nenhuma, para a bandeja “Sem sala no mapa”.";
  }
  if (!recebe && receptor && receptor.id !== setorId) {
    return `Os ambientes continuam chegando em "${receptor.nome}". Esta sala recebe o que você mover à mão.`;
  }
  if (recebe && outras.length > 0) {
    return `Outras ${outras.length === 1 ? "sala usa" : `${outras.length} salas usam`} esta etapa; a recepção fica aqui.`;
  }
  return null;
}

/**
 * `modo` = "novo" | "editar"; `valores` = {nome, etapa, recebe_da_planilha} do
 * setor em edição (ignorado no modo novo); `onSalvar(dados)` recebe exatamente
 * os campos do corpo da requisição e deve devolver uma Promise.
 */
export default function FormularioSetor({
  aberto,
  modo = "novo",
  setorId = null,
  valores = null,
  catalogo,
  etapaSugerida = "",
  erro = null,
  onFechar,
  onSalvar,
  onRemover = null,
  onExcluir = null,
}) {
  const [campos, setCampos] = useState(vazio);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const grupos = useMemo(gruposDeVinculo, []);

  // Reabrir o painel recomeça do zero (ou do setor em edição): sem isto o nome
  // do setor anterior ficaria no campo do próximo.
  useEffect(() => {
    if (!aberto) return;
    setTentouSalvar(false);
    // Reabrir recomeça do zero: o "tem certeza?" de uma sala não pode valer
    // para a próxima que o gestor abrir.
    setConfirmandoExclusao(false);
    setCampos(valores ? { ...vazio(), ...valores } : { ...vazio(), etapa: etapaSugerida || "" });
  }, [aberto, valores, etapaSugerida]);

  const definir = (campo) => (evento) => {
    const valor = evento.target.type === "checkbox" ? evento.target.checked : evento.target.value;
    setCampos((atuais) => ({ ...atuais, [campo]: valor }));
  };

  const nome = campos.nome.trim();
  const semNome = nome.length === 0;
  const semEtapa = !campos.etapa;
  const aviso = avisoDaRecepcao({ etapa: campos.etapa, recebe: campos.recebe_da_planilha, catalogo, setorId });

  const salvar = async () => {
    setTentouSalvar(true);
    if (semNome || semEtapa) return;
    await onSalvar({ nome, etapa: campos.etapa, recebe_da_planilha: campos.recebe_da_planilha });
  };

  const titulo = modo === "editar" ? "Editar setor" : "Novo setor";
  const subtitulo = modo === "editar"
    ? "O nome e a etapa valem para todo mundo que abre o mapa."
    : "A sala já está desenhada. Falta dizer como ela se chama e o que chega nela.";

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title={titulo}
      subtitle={subtitulo}
      width="min(92vw, 460px)"
      // Id próprio: a bandeja também é um SlideOverPanel e os dois podem ficar
      // montados ao mesmo tempo (o painel demora a sair). Com o id padrão, os
      // dois títulos teriam o MESMO id e o leitor de tela anunciaria o errado.
      ariaLabelledBy="mapa-form-setor-titulo"
      footer={
        <RodapeDoSetor>
          <SecondaryButton type="button" onClick={onFechar}>Cancelar</SecondaryButton>
          <AsyncButton
            onClick={salvar}
            successMessage={modo === "editar" ? "Setor atualizado." : "Setor criado."}
            silentError
          >
            {modo === "editar" ? "Salvar setor" : "Criar setor"}
          </AsyncButton>
        </RodapeDoSetor>
      }
    >
      <FormularioDoSetor
        onSubmit={(e) => {
          e.preventDefault();
          salvar();
        }}
      >
        <CampoDoSetor>
          <label htmlFor="mapa-setor-nome">Nome do setor</label>
          <input
            id="mapa-setor-nome"
            type="text"
            value={campos.nome}
            maxLength={LIMITE_NOME}
            autoComplete="off"
            placeholder="Como a fábrica chama esta sala"
            aria-invalid={tentouSalvar && semNome ? "true" : undefined}
            aria-describedby={tentouSalvar && semNome ? "mapa-setor-nome-erro" : undefined}
            onChange={definir("nome")}
          />
          {tentouSalvar && semNome && (
            <p className="erro" id="mapa-setor-nome-erro">Dê um nome ao setor.</p>
          )}
        </CampoDoSetor>

        <CampoDoSetor>
          <label htmlFor="mapa-setor-etapa">Etapa da planilha</label>
          <select
            id="mapa-setor-etapa"
            value={campos.etapa}
            aria-invalid={tentouSalvar && semEtapa ? "true" : undefined}
            aria-describedby={tentouSalvar && semEtapa ? "mapa-setor-etapa-erro" : undefined}
            onChange={definir("etapa")}
          >
            <option value="">Escolha a etapa</option>
            {grupos.map((grupo) => (
              <optgroup key={grupo.rotulo} label={grupo.rotulo}>
                {grupo.opcoes.map((opcao) => (
                  <option key={opcao.valor} value={opcao.valor}>{opcao.texto}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="ajuda">
            É a coluna do gerenciador de produção que faz o ambiente andar. Escolha o grupo inteiro
            quando a sala cuida de todas as etapas dele. Vários setores podem usar a mesma etapa.
          </p>
          {vinculoSoParalelo(campos.etapa) && (
            <p className="ajuda">
              Esta etapa corre em paralelo ao fluxo: nenhum ambiente para aqui sozinho. A sala
              recebe o que você mover à mão e as peças desmembradas.
            </p>
          )}
          {tentouSalvar && semEtapa && (
            <p className="erro" id="mapa-setor-etapa-erro">Escolha a etapa que alimenta este setor.</p>
          )}
        </CampoDoSetor>

        <CampoDoSetor data-tipo="marcavel">
          <label htmlFor="mapa-setor-recebe">
            <input
              id="mapa-setor-recebe"
              type="checkbox"
              checked={campos.recebe_da_planilha}
              onChange={definir("recebe_da_planilha")}
            />
            <span>Este setor recebe os ambientes desta etapa</span>
          </label>
          {aviso && <p className="ajuda">{aviso}</p>}
        </CampoDoSetor>

        {erro && (
          <AvisoForm role="alert">
            <FiAlertTriangle aria-hidden="true" />
            <span>{erro}</span>
          </AvisoForm>
        )}

        {modo === "editar" && onExcluir && (
          <CampoDoSetor data-tipo="perigo">
            <span className="rotulo">Excluir do mapa</span>
            <p className="ajuda">
              Esta sala nunca recebeu trabalho: dá para apagar de vez, como quem desfaz um risco.
              {confirmandoExclusao ? " Não dá para desfazer." : ""}
            </p>
            {confirmandoExclusao ? (
              <>
                <AsyncButton
                  variant="danger"
                  successMessage="Setor excluído."
                  silentError
                  onClick={onExcluir}
                >
                  Excluir “{valores?.nome || "este setor"}” mesmo
                </AsyncButton>
                <SecondaryButton type="button" onClick={() => setConfirmandoExclusao(false)}>
                  Manter
                </SecondaryButton>
              </>
            ) : (
              <AsyncButton variant="danger" silentError onClick={async () => setConfirmandoExclusao(true)}>
                Excluir “{valores?.nome || "este setor"}”
              </AsyncButton>
            )}
          </CampoDoSetor>
        )}

        {modo === "editar" && !onExcluir && onRemover && (
          <CampoDoSetor data-tipo="perigo">
            <span className="rotulo">Tirar do mapa</span>
            <p className="ajuda">
              A sala sai do desenho e para de receber ambientes. O histórico continua: nada é apagado.
              {campos.etapa
                ? ` Sem outra sala nesta etapa, os ambientes de "${rotuloDaEtapa(campos.etapa)}" vão para a sala vizinha do fluxo, com o selo “aguardando”.`
                : ""}
            </p>
            <AsyncButton variant="danger" onClick={onRemover} successMessage="Setor tirado do mapa." silentError>
              Tirar “{valores?.nome || "este setor"}” do mapa
            </AsyncButton>
          </CampoDoSetor>
        )}
      </FormularioDoSetor>
    </SlideOverPanel>
  );
}
